import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Key,
  Bell,
  Moon,
  Sun,
  Globe,
  Save,
  CreditCard,
  AlertTriangle,
  Check,
  Lock,
  Mail,
  Star,
  User,
  Briefcase,
  Phone,
  Download,
  ExternalLink,
  FileText
} from 'lucide-react';
import { ThemeContext } from '../../context/ThemeContext';
import { getCurrentUser, getProfile } from '../../services/auth';
import api from '../../services/api';
import stripeService from '../../services/stripe';
import { checkReviewEligibility } from '../../services/reviews';

const VALID_TABS = ['profile', 'general', 'notifications', 'security', 'payment'];

const SettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasExistingReview, setHasExistingReview] = useState(false);
  const [reviewEligibility, setReviewEligibility] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [invoicesData, setInvoicesData] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const successTimeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Get tab from URL query params
  const [activeTab, setActiveTab] = useState(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    return VALID_TABS.includes(tabParam) ? tabParam : 'profile';
  });

  // Sync activeTab with URL changes (for navbar navigation while on settings page)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    const newTab = VALID_TABS.includes(tabParam) ? tabParam : 'profile';
    setActiveTab(newTab);
  }, [location.search]);

  // Profile state
  const [profileSaving, setProfileSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Account Information
    email: '',

    // Personal Information
    firstName: '',
    lastName: '',
    personalPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',

    // Business Information
    companyName: '',
    jobTitle: '',
    businessPhone: '',
    businessSize: 'small',
    employeeCount: '',
    industry: '',
  });
  
  const [settings, setSettings] = useState({
    email_notifications: true,
    product_updates: true,
    security_alerts: true,
    marketing_emails: false,
    language: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    two_factor_auth: false
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();

        if (!user) {
          navigate('/login');
          return;
        }

        // Try to get the latest profile data from the API
        try {
          const profile = await getProfile();
          setFormData({
            // Account Information
            email: profile.email || '',

            // Personal Information
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            personalPhone: profile.personalPhone || '',
            address: profile.address || '',
            city: profile.city || '',
            state: profile.state || '',
            zipCode: profile.zipCode || '',

            // Business Information
            companyName: profile.companyName || '',
            jobTitle: profile.jobTitle || '',
            businessPhone: profile.businessPhone || '',
            businessSize: profile.businessSize || 'small',
            employeeCount: profile.employeeCount || '',
            industry: profile.industry || '',
          });
        } catch (error) {
          // Fallback to stored user data if API fails
          setFormData({
            // Account Information
            email: user.email || '',

            // Personal Information
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            personalPhone: user.personalPhone || '',
            address: user.address || '',
            city: user.city || '',
            state: user.state || '',
            zipCode: user.zipCode || '',

            // Business Information
            companyName: user.companyName || '',
            jobTitle: user.jobTitle || '',
            businessPhone: user.businessPhone || '',
            businessSize: user.businessSize || 'small',
            employeeCount: user.employeeCount || '',
            industry: user.industry || '',
          });
        }

        // Try to get the latest settings data from the API
        try {
          const response = await api.get('/User/settings');
          const data = response.data;
          // Auto-detect timezone from browser if not set
          if (!data.timezone) {
            try {
              data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            } catch {
              data.timezone = 'UTC';
            }
          }
          setSettings(data);
        } catch (error) {
          // Fallback to default settings if API fails
          console.error('Failed to fetch user settings:', error);
          // Keep the default settings
        }

        // Check if user has an existing review
        try {
          const eligibility = await checkReviewEligibility();
          setReviewEligibility(eligibility);
          setHasExistingReview(eligibility.existingReview && eligibility.existingReview.submittedAt !== null);
        } catch (error) {
          // If API fails, assume no review
          console.error('Failed to check review eligibility:', error);
        }
      } catch (err) {
        setError('Failed to load settings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [navigate]);

  const handleToggle = (setting) => {
    setSettings({
      ...settings,
      [setting]: !settings[setting]
    });
    
    // Clear success message when settings are changed
    if (success) {
      setSuccess(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings({
      ...settings,
      [name]: value
    });

    // Clear success message when settings are changed
    if (success) {
      setSuccess(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.substring(0, 10);
    const match = limited.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      let formatted = '';
      if (match[1]) formatted += `(${match[1]}`;
      if (match[1].length === 3) formatted += ') ';
      if (match[2]) formatted += match[2];
      if (match[2].length === 3) formatted += '-';
      if (match[3]) formatted += match[3];
      return formatted;
    }
    return value;
  };

  const handlePhoneKeyDown = (e) => {
    if (e.key !== 'Backspace') return;
    const input = e.target;
    if (input.selectionStart !== input.selectionEnd) return;
    const val = input.value;
    const pos = input.selectionStart;
    const charBefore = val[pos - 1];
    if (charBefore === ' ' || charBefore === '-' || charBefore === ')') {
      e.preventDefault();
      let digitPos = pos - 1;
      while (digitPos > 0 && !/\d/.test(val[digitPos - 1])) {
        digitPos--;
      }
      if (digitPos > 0) {
        const newValue = val.slice(0, digitPos - 1) + val.slice(pos);
        const syntheticEvent = { target: { name: input.name, value: newValue } };
        handleProfileChange(syntheticEvent);
      }
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'personalPhone' || name === 'businessPhone') {
      newValue = formatPhoneNumber(value);
    }
    setFormData({
      ...formData,
      [name]: newValue,
    });

    // Clear success message when form is changed
    if (success) {
      setSuccess(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      setProfileSaving(true);
      setError(null);

      // Make API call to update profile
      await api.put('/User/profile', formData);

      // Update local storage with new user data
      const user = getCurrentUser();
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setSuccess(true);

      // Hide success message after 3 seconds
      successTimeoutRef.current = setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      // Make API call to update settings
      await api.put('/User/settings', settings);

      setSuccess(true);

      // Hide success message after 3 seconds
      successTimeoutRef.current = setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };



  const handleChangePassword = () => {
    // Navigate to password change page or show modal
    navigate('/account/change-password');
  };

  // Fetch payment data when payment tab is active
  const fetchPaymentData = async () => {
    setPaymentLoading(true);
    try {
      const [subRes, billingRes, invoicesRes] = await Promise.allSettled([
        stripeService.getSubscriptionDetails(),
        stripeService.getBillingInfo(),
        stripeService.getInvoices(),
      ]);
      if (subRes.status === 'fulfilled') setSubscriptionData(subRes.value);
      if (billingRes.status === 'fulfilled') setBillingData(billingRes.value);
      if (invoicesRes.status === 'fulfilled') setInvoicesData((invoicesRes.value || []).slice(0, 5));
    } catch (err) {
      console.error('Failed to fetch payment data:', err);
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'payment') {
      fetchPaymentData();
    }
  }, [activeTab]);

  // Payment tab helpers
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(dateString));
  };

  const formatCurrency = (amount, currency = 'usd') => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  };

  const getBillingCycleLabel = (billingCycle) => {
    if (!billingCycle) return 'N/A';
    const map = { '1 month': 'Monthly', '3 months': 'Every 3 Months', '6 months': 'Every 6 Months', '1 year': 'Annual' };
    return map[billingCycle] || billingCycle;
  };

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const user = getCurrentUser();
      const portalData = await stripeService.createCustomerPortalSession(user.id.toString());
      const portalUrl = portalData.url || portalData;
      const url = new URL(portalUrl);
      if (!url.hostname.endsWith('.stripe.com') && url.hostname !== 'stripe.com') {
        throw new Error('Invalid portal URL');
      }
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      setError('Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const baseTimezones = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
    { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
    { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
    { value: 'Europe/London', label: 'London' },
    { value: 'Europe/Paris', label: 'Paris' },
    { value: 'Asia/Tokyo', label: 'Tokyo' },
    { value: 'Australia/Sydney', label: 'Sydney' }
  ];

  // Include the user's current timezone if it's not in the base list
  const timezones = baseTimezones.some(tz => tz.value === settings.timezone)
    ? baseTimezones
    : [{ value: settings.timezone, label: `${settings.timezone.replace(/_/g, ' ')} (Detected)` }, ...baseTimezones];

  const renderTabContent = () => {
    switch(activeTab) {
      case 'profile':
        return (
          <form onSubmit={handleProfileSubmit}>
            <div className="space-y-4">
              {/* Account Information Section */}
              <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Account Information</h2>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleProfileChange}
                        className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                        placeholder="john.doe@example.com"
                      />
                    </div>
                  </div>

                </div>
              </div>

              {/* Personal Information Section */}
              <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Personal Information</h2>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        value={formData.firstName}
                        onChange={handleProfileChange}
                        className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                        placeholder="John"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        value={formData.lastName}
                        onChange={handleProfileChange}
                        className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="personalPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Personal Phone
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        name="personalPhone"
                        id="personalPhone"
                        value={formData.personalPhone}
                        onChange={handleProfileChange}
                        onKeyDown={handlePhoneKeyDown}
                        className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Address
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        name="address"
                        id="address"
                        value={formData.address}
                        onChange={handleProfileChange}
                        className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                        placeholder="123 Main St"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2 grid grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        City
                      </label>
                      <div className="relative rounded-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          name="city"
                          id="city"
                          value={formData.city}
                          onChange={handleProfileChange}
                          className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                          placeholder="San Francisco"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        State
                      </label>
                      <div className="relative rounded-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          name="state"
                          id="state"
                          value={formData.state}
                          onChange={handleProfileChange}
                          className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                          placeholder="CA"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ZIP Code
                      </label>
                      <div className="relative rounded-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          name="zipCode"
                          id="zipCode"
                          value={formData.zipCode}
                          onChange={handleProfileChange}
                          className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                          placeholder="94103"
                        />
                      </div>
                    </div>
                </div>
              </div>
            </div>

            {/* Work Information Section */}
            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Work Information</h2>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company Name
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Briefcase className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="companyName"
                      id="companyName"
                      value={formData.companyName}
                      onChange={handleProfileChange}
                      className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                      placeholder="Your Auto Repair Shop"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Job Title
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Briefcase className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="jobTitle"
                      id="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleProfileChange}
                      className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                      placeholder="Shop Owner"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="businessPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Business Phone Number
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="businessPhone"
                      id="businessPhone"
                      value={formData.businessPhone}
                      onChange={handleProfileChange}
                      onKeyDown={handlePhoneKeyDown}
                      className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="businessSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Business Size
                    </label>
                    <div className="relative rounded-md">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                        </svg>
                      </div>
                      <select
                        id="businessSize"
                        name="businessSize"
                        value={formData.businessSize}
                        onChange={handleProfileChange}
                        className="pl-10 pr-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11 appearance-none"
                      >
                        <option value="sole_proprietor">Sole Proprietor</option>
                        <option value="small">Small Business (1-10 employees)</option>
                        <option value="medium">Medium Business (11-50 employees)</option>
                        <option value="large">Large Business (51+ employees)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="employeeCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Number of Employees
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <input
                        type="number"
                        id="employeeCount"
                        name="employeeCount"
                        value={formData.employeeCount}
                        onChange={handleProfileChange}
                        className="pl-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11"
                        placeholder="5"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Industry
                  </label>
                  <div className="relative rounded-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                      </svg>
                    </div>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleProfileChange}
                      className="pl-10 pr-10 bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-400 dark:border-gray-600 rounded-md text-gray-900 dark:text-white transition-colors h-11 appearance-none"
                    >
                      <option value="">Select Industry</option>
                      <option value="automotive">Automotive</option>
                      <option value="repair">Repair & Maintenance</option>
                      <option value="service">Service Center</option>
                      <option value="dealership">Dealership</option>
                      <option value="parts">Auto Parts</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              type="button"
              className="mr-3 inline-flex items-center px-5 py-2.5 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={profileSaving}
              className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-70"
            >
              {profileSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
        );

      case 'general':
        return (
          <div className="space-y-4">
            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Timezone</h3>
              <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select your timezone
              </label>
              <select
                id="timezone"
                name="timezone"
                value={settings.timezone}
                onChange={handleChange}
                className="block w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-2.5 px-4 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white transition-colors h-11 appearance-none bg-no-repeat bg-right"
                style={{
                  backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E\")",
                  backgroundSize: "1.5em 1.5em",
                  backgroundPosition: "right 0.5rem center",
                  paddingRight: "2.5rem"
                }}
              >
                {timezones.map((timezone) => (
                  <option key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Product Review</h3>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {hasExistingReview ? 'Manage your review' : 'Share your experience with Stonewall Invoicing'}
                  </p>
                </div>
                {reviewEligibility?.isEligible || hasExistingReview ? (
                  <Link
                    to="/account/review"
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <Star className="h-4 w-4 mr-1.5" />
                    {hasExistingReview ? 'Edit Review' : 'Leave a Review'}
                  </Link>
                ) : (
                  <button
                    disabled
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60"
                  >
                    <Star className="h-4 w-4 mr-1.5" />
                    Leave a Review
                  </button>
                )}
              </div>
              {reviewEligibility && !reviewEligibility.isEligible && !hasExistingReview && (
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 italic">
                  {reviewEligibility.reason || 'You may leave a review after 14 days of using our product!'}
                </p>
              )}
            </div>

            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Theme</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {darkMode ? 'Currently using dark theme' : 'Currently using light theme'}
                  </p>
                </div>
                <button
                  onClick={toggleDarkMode}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  {darkMode ? (
                    <>
                      <Sun className="h-4 w-4 mr-1.5" />
                      Light Mode
                    </>
                  ) : (
                    <>
                      <Moon className="h-4 w-4 mr-1.5" />
                      Dark Mode
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
        
      case 'notifications':
        return (
          <div className="space-y-4">
            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Email Notifications</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Receive notifications via email</p>
                </div>
                <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="email_notifications"
                    checked={settings.email_notifications}
                    onChange={() => handleToggle('email_notifications')}
                    className="sr-only"
                  />
                  <label
                    htmlFor="email_notifications"
                    className={`flex items-center w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                      settings.email_notifications ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block bg-white dark:bg-white rounded-full h-5 w-5 transform transition-transform duration-200 ease-in-out ${
                        settings.email_notifications ? 'translate-x-[26px]' : 'translate-x-0.5'
                      }`}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Product Updates</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Receive updates about product features</p>
                </div>
                <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="product_updates"
                    checked={settings.product_updates}
                    onChange={() => handleToggle('product_updates')}
                    className="sr-only"
                  />
                  <label
                    htmlFor="product_updates"
                    className={`flex items-center w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                      settings.product_updates ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block bg-white dark:bg-white rounded-full h-5 w-5 transform transition-transform duration-200 ease-in-out ${
                        settings.product_updates ? 'translate-x-[26px]' : 'translate-x-0.5'
                      }`}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Security Alerts</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Receive alerts about security issues</p>
                </div>
                <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="security_alerts"
                    checked={settings.security_alerts}
                    onChange={() => handleToggle('security_alerts')}
                    className="sr-only"
                  />
                  <label
                    htmlFor="security_alerts"
                    className={`flex items-center w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                      settings.security_alerts ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block bg-white dark:bg-white rounded-full h-5 w-5 transform transition-transform duration-200 ease-in-out ${
                        settings.security_alerts ? 'translate-x-[26px]' : 'translate-x-0.5'
                      }`}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Marketing Emails</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Receive marketing and promotional emails</p>
                </div>
                <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="marketing_emails"
                    checked={settings.marketing_emails}
                    onChange={() => handleToggle('marketing_emails')}
                    className="sr-only"
                  />
                  <label
                    htmlFor="marketing_emails"
                    className={`flex items-center w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                      settings.marketing_emails ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block bg-white dark:bg-white rounded-full h-5 w-5 transform transition-transform duration-200 ease-in-out ${
                        settings.marketing_emails ? 'translate-x-[26px]' : 'translate-x-0.5'
                      }`}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'security':
        return (
          <div className="space-y-4">
            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Password</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Change your account password</p>
                </div>
                <button
                  onClick={handleChangePassword}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <Key className="h-4 w-4 mr-1.5" />
                  Change Password
                </button>
              </div>
            </div>

            {/* Two-Factor Authentication */}
            <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Two-Factor Authentication</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Add an extra layer of security to your account</p>
                </div>
                <button
                  disabled
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60"
                >
                  <Lock className="h-4 w-4 mr-1.5" />
                  Coming Soon
                </button>
              </div>
            </div>

          </div>
        );
        
      case 'payment':
        return (
          <div className="space-y-4">
            {paymentLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
              </div>
            ) : (
              <>
                {/* Current Plan */}
                <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Current Plan</h3>
                  {subscriptionData?.hasActiveSubscription ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {subscriptionData.planTier || 'Professional'} Plan
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                              {formatCurrency(subscriptionData.amount, subscriptionData.currency)}/{getBillingCycleLabel(subscriptionData.billingCycle).toLowerCase()}
                            </span>
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {subscriptionData.cancelAtPeriodEnd
                              ? `Cancels ${formatDate(subscriptionData.currentPeriodEnd)}`
                              : billingData?.nextBillingDate
                                ? `Renews ${formatDate(billingData.nextBillingDate)}`
                                : `Active since ${formatDate(subscriptionData.currentPeriodStart)}`
                            }
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            subscriptionData.cancelAtPeriodEnd
                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                              : subscriptionData.status === 'active'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {subscriptionData.cancelAtPeriodEnd ? 'Canceling' : subscriptionData.status === 'active' ? 'Active' : subscriptionData.status}
                          </span>
                          <Link
                            to="/account/subscription"
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            Change Plan
                          </Link>
                        </div>
                      </div>
                      {subscriptionData.cancelAtPeriodEnd && (
                        <p className="text-xs text-orange-600 dark:text-orange-400">
                          Access continues until {formatDate(subscriptionData.currentPeriodEnd)}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600 dark:text-gray-400">No active subscription</p>
                      <Link
                        to="/account/subscription"
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        View Plans
                      </Link>
                    </div>
                  )}
                </div>

                {/* Payment Method */}
                <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Payment Method</h3>
                    <button
                      onClick={openCustomerPortal}
                      disabled={portalLoading}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
                    >
                      <ExternalLink className="h-4 w-4 mr-1.5" />
                      {portalLoading ? 'Opening...' : 'Manage'}
                    </button>
                  </div>
                  {billingData?.hasValidPaymentMethod && billingData.defaultPaymentMethod ? (
                    <div className="flex items-center py-3">
                      <div className="w-10 h-10 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center mr-3">
                        {billingData.defaultPaymentMethod.brand?.toLowerCase() === 'visa' ? (
                          <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">VISA</span>
                        ) : billingData.defaultPaymentMethod.brand?.toLowerCase() === 'mastercard' ? (
                          <span className="text-red-600 dark:text-red-400 font-bold text-sm">MC</span>
                        ) : billingData.defaultPaymentMethod.brand?.toLowerCase() === 'amex' ? (
                          <span className="text-blue-800 dark:text-blue-300 font-bold text-xs">AMEX</span>
                        ) : (
                          <CreditCard className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {billingData.defaultPaymentMethod.brand
                            ? billingData.defaultPaymentMethod.brand.charAt(0).toUpperCase() + billingData.defaultPaymentMethod.brand.slice(1)
                            : 'Card'}{' '}
                          ending in {billingData.defaultPaymentMethod.last4}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Expires {billingData.defaultPaymentMethod.expMonth}/{billingData.defaultPaymentMethod.expYear}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-3">
                      Payment managed via Stripe.
                    </p>
                  )}
                </div>

                {/* Recent Invoices */}
                <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Invoices</h3>
                    <Link
                      to="/account/billing-history"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      View all
                    </Link>
                  </div>
                  {invoicesData.length > 0 ? (
                    <div className="space-y-2">
                      {invoicesData.map((invoice) => (
                        <div
                          key={invoice.invoiceId}
                          className="flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex items-center min-w-0">
                            <FileText className="h-4 w-4 text-gray-400 mr-2.5 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm text-gray-900 dark:text-white truncate">
                                {invoice.invoiceNumber || 'Invoice'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(invoice.invoiceDate)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center ml-4">
                            <span className={`text-sm font-medium mr-3 ${
                              invoice.status === 'paid'
                                ? 'text-green-600 dark:text-green-400'
                                : invoice.status === 'open'
                                  ? 'text-yellow-600 dark:text-yellow-400'
                                  : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {formatCurrency(invoice.total, invoice.currency)}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              invoice.status === 'paid'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : invoice.status === 'open'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : 'N/A'}
                            </span>
                            {invoice.invoicePdfUrl && (
                              <a
                                href={invoice.invoicePdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                                title="Download PDF"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-3">
                      No invoices yet.
                    </p>
                  )}
                </div>

              </>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col flex-1 bg-gray-100 dark:bg-gray-900 color-transition">
      <div className="flex-grow container mx-auto px-4 sm:px-5 lg:px-7 py-10">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
          ) : (
            <div className="bg-gray-200 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition">
              <div className="px-6 py-5 bg-gray-300 dark:bg-gray-700 border-b border-gray-400 dark:border-gray-600">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Account Settings</h1>
              </div>
              
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 m-6 px-4 py-3 rounded-md flex items-start">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              )}
              
              {success && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 m-6 px-4 py-3 rounded-md flex items-start">
                  <div className="flex-shrink-0 mt-0.5">
                    <Check className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">Settings saved successfully!</p>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row">
                {/* Sidebar */}
                <div className="w-full sm:w-48 border-b sm:border-b-0 sm:border-r border-gray-400 dark:border-gray-600">
                  <nav className="flex sm:flex-col overflow-x-auto sm:overflow-visible divide-x sm:divide-x-0 sm:divide-y divide-gray-400 dark:divide-gray-600">
                    {[
                      { key: 'profile', label: 'Profile', icon: User },
                      { key: 'general', label: 'General', icon: Globe },
                      { key: 'notifications', label: 'Notifications', icon: Bell },
                      { key: 'security', label: 'Security', icon: Lock },
                      { key: 'payment', label: 'Payment', icon: CreditCard },
                    ].map(({ key, label, icon: Icon }) => (
                      <button
                        key={key}
                        onClick={() => {
                          setActiveTab(key);
                          navigate(`/account/settings?tab=${key}`, { replace: true });
                        }}
                        className={`flex items-center justify-center sm:justify-start px-4 py-2.5 text-sm font-medium w-full transition-colors ${
                          activeTab === key
                            ? 'text-gray-900 dark:text-white bg-gray-400/40 dark:bg-gray-700/50'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-400/40 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        <Icon className={`mr-3 h-4 w-4 ${
                          activeTab === key
                            ? 'text-gray-900 dark:text-white'
                            : 'text-gray-500 dark:text-gray-400'
                        }`} />
                        {label}
                      </button>
                    ))}
                  </nav>
                </div>
              
                {/* Main content */}
                <div className="flex-1 p-6">
                  {renderTabContent()}

                  {activeTab !== 'security' && activeTab !== 'profile' && activeTab !== 'payment' && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                      <button
                        onClick={handleSaveSettings}
                        disabled={saving}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-70"
                      >
                        {saving ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
    </div>
  );
};

export default SettingsPage;